import { stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable, observable, toJS } from 'mobx';

import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { AppStore } from '../../App/stores/AppStore.js';
import { FilterPropertyStore } from '../../App/stores/FilterPropertyStore.js';
import { GlobalInputsStore } from '../../App/stores/GlobalInputsStore.js';
import { InputStoreBase } from '../../shared/InputStoreBase.js';
import { IDropdownComponentProps, IDropdownOptions } from '../models/index.js';
type IDropdownItem = components.base.Dropdown.models.IDropdownItem;
type IDropdownProps = components.base.Dropdown.models.IDropdownProps;

export type TDropdownValue = IDropdownItem | IDropdownItem[] | string | string[];

export class DropdownStore extends InputStoreBase<
	IDropdownOptions,
	IDropdownComponentProps,
	IDropdownItem | IDropdownItem[]
> {
	static componentKey = 'dropdown' as const;
	@observable.ref items: IDropdownItem[] = [];

	constructor(
		storeContext: TStoreContext,
		manifest: IManifest,
		inputKey: string,
		options: IDropdownOptions,
		componentProps: IDropdownComponentProps,
	) {
		super(storeContext, manifest, inputKey, options, componentProps);
		makeObservable(this);
	}

	@computed
	get filterPropertyStore(): FilterPropertyStore {
		return this.storeContext.get<FilterPropertyStore>('filterPropertyStore');
	}

	@action
	initializeItems = async () => {
		await this.dropdownItems();
		this.value = this.initializeValue();
	};

	initializeValue = (): IDropdownItem | IDropdownItem[] | undefined => {
		const appStore = this.storeContext.get<AppStore>('appStore');
		const globalInputsStore = this.storeContext.get<GlobalInputsStore>('globalInputsStore');
		if (appStore.loadInitialValuesFromStorage) {
			const valueFromStorage = globalInputsStore.getValue(this.inputKey);
			if (valueFromStorage) {
				return this.initializeValueForComponent(valueFromStorage);
			}
		}
		const optionItems = this.items;
		const defaultValue = this.queryDefaultValue;
		if (defaultValue) {
			const selectedItems = this.isMultiSelect
				? this.parseMultiSelectDefaults(defaultValue, optionItems)
				: this.parseSelectDefault(defaultValue, optionItems);
			this.setValue(selectedItems);
			return this.initializeValueForComponent(selectedItems);
		}
	};

	parseSelectDefault = (
		rawDefault: string | IDropdownItem,
		optionItems: IDropdownItem[],
	): IDropdownItem | undefined => {
		const defaultValue = this.getItemValue(rawDefault);
		const matchingItem = this.findMatchingDefaultItem(defaultValue, optionItems);
		this.storeContext
			.get<AppStore>('appStore')
			.logDebug('Dropdown Store > parse select default', { defaultValue, matchingItem });
		if (matchingItem) {
			return matchingItem;
		}
		this.logMissingDefaultWarning([defaultValue]);
	};

	parseMultiSelectDefaults = (
		rawDefaults: string | string[] | IDropdownItem | IDropdownItem[],
		optionItems: IDropdownItem[],
	): IDropdownItem[] => {
		const defaultItems = [...(Array.isArray(rawDefaults) ? rawDefaults : [rawDefaults])];
		const missingValues: string[] = [];
		const matchingItems: IDropdownItem[] = [];
		defaultItems.forEach((defaultItem) => {
			const defaultValue = this.getItemValue(defaultItem);
			const matchingItem = this.findMatchingDefaultItem(defaultValue, optionItems);
			this.storeContext
				.get<AppStore>('appStore')
				.logDebug('Dropdown Store > parse multi-select default', { defaultValue, matchingItem });
			if (matchingItem) {
				matchingItems.push(matchingItem);
			} else {
				missingValues.push(defaultValue);
			}
		});
		this.logMissingDefaultWarning(missingValues);
		return matchingItems;
	};

	getItemValue = (item: string | IDropdownItem): string => {
		return typeof item === 'string' ? item : item.value;
	};

	findMatchingDefaultItem = (defaultValue: string, optionItems: IDropdownItem[]): IDropdownItem | undefined => {
		if (optionItems.length > 0) {
			return optionItems.find((optionItem) => optionItem.value === defaultValue);
		}
	};

	logMissingDefaultWarning = (missingValues: string[]) => {
		// Displays a warning message for all the items that should be defaulted but do not exist in the dropdown
		if (missingValues.length !== 0) {
			const warningMessage = `Unable to apply default value of ${missingValues.join(
				',',
			)} to the input dropdown for '${this.inputKey}' as the value(s) doesn't exist in the available list`;
			console.warn(warningMessage);
		}
	};

	initializeValueForComponent = (value?: TDropdownValue): IDropdownItem | IDropdownItem[] | undefined => {
		if (value) {
			if (Array.isArray(value)) {
				return (value as any[]).map((v) => {
					return this.getValueAsDropdownItem(v);
				});
			}
			return this.getValueAsDropdownItem(value);
		}
	};

	getValueAsDropdownItem = (value: string | IDropdownItem): IDropdownItem => {
		if (typeof value === 'string') {
			return {
				value,
				label: value,
			} as IDropdownItem;
		}
		return value;
	};

	@action
	setValue = (value?: IDropdownItem | IDropdownItem[]) => {
		const { filter } = this.options;
		this.value = value;
		const globalInputsStore = this.storeContext.get<GlobalInputsStore>('globalInputsStore');
		const sanitizedValue = this.sanitizeValue(value);
		let usingFilters = false;
		if (filter) {
			const filterValue = Array.isArray(value) ? value : [value];
			if (filterValue) {
				this.filterPropertyStore.updateFilterValueSelections(filter, filterValue);
				usingFilters = true;
			}
		}
		globalInputsStore.setValue(this.inputKey, sanitizedValue, !usingFilters);
	};

	sanitizeValue = (value?: IDropdownItem | IDropdownItem[]): TDropdownValue => {
		const { simplifyValue = false } = this.options;
		const sanitizedValue = toJS(value) || [];
		if (Array.isArray(sanitizedValue)) {
			if (simplifyValue) {
				return sanitizedValue.map((value) => value.value);
			}
			return sanitizedValue.map((value) => {
				return {
					label: value.label,
					value: value.value,
				};
			});
		}
		if (simplifyValue) {
			return sanitizedValue.value;
		}
		return {
			label: sanitizedValue.label,
			value: sanitizedValue.value,
		};
	};

	@computed
	get hasData(): boolean {
		if (this.options?.noDataOptions !== undefined) {
			const items = this.items;
			return !!(items && items.length > 0);
		}
		return true;
	}

	@computed
	get labelValue(): string {
		const { label, labelQuery } = this.options;
		let value: string = label || '';
		if (labelQuery) {
			value = this.getQueryValue(labelQuery);
		}
		return value;
	}

	@computed
	get pillValue(): string {
		const { pillLabel } = this.options;
		const translationStore =
			this.storeContext.get<stores.TranslationStore<IConfiguration, IComponentStyles>>('translationStore');
		const selected = this.selectionsCount;
		const total = this.items.length;
		const labelFormat = pillLabel || '{selected}/{total}';
		return translationStore.translate(labelFormat, { selected, total });
	}

	@action
	getFilterValues(): IDropdownItem[] {
		const { filter } = this.options;
		const filterValues = this.filterPropertyStore.getFilterPropertyValues(filter);
		return filterValues as IDropdownItem[];
	}

	@action
	dropdownItems = async () => {
		const { items, itemsQuery, filter } = this.options;
		let values: IDropdownItem[] = items || [];
		if (itemsQuery) {
			values = this.getQueryValue(itemsQuery);
		}
		if (filter) {
			values = this.getFilterValues();
		}
		this.items = values;
	};

	@computed
	get queryDefaultValue() {
		const { defaultValue } = this.options;
		if (defaultValue) {
			return this.getQueryValue(defaultValue);
		}
	}

	@computed
	get isMultiSelect(): boolean {
		const appStore = this.storeContext.get<AppStore>('appStore');
		const { dropdown } = this.componentProps;
		return (dropdown && dropdown.isMultiSelect && !appStore.redirectConfiguration) || false;
	}

	@computed
	get selectionsCount(): number {
		// Convert this.value to js as we cannot run Array.isArray on an observable
		const value = toJS(this.value);
		return Array.isArray(value) ? value.length : value ? 1 : 0;
	}

	@computed
	get dropdownProps(): IDropdownProps {
		const { filter } = this.options;
		const { redirectConfiguration, redirectHelper, appParamsHelper } = this.storeContext.get<AppStore>('appStore');
		return {
			...this.componentProps.dropdown,
			items: this.items,
			onChange: (item) => {
				const sanitizedValue = this.sanitizeValue(item);
				this.setValue(item);
				if (redirectConfiguration && common.commonUtils.isObject(sanitizedValue)) {
					redirectHelper.go(redirectConfiguration, {
						...appParamsHelper.inputs,
						[this.inputKey]: sanitizedValue['value'],
					});
				}
			},
			selectedItem: filter ? this.filterValue : this.value,
		};
	}

	@computed
	get filterValue(): IDropdownItem[] {
		const { filterValues } = this.filterPropertyStore;
		const filterConfig = this.options.filter;
		if (!filterConfig || !filterValues) {
			return;
		}
		const filterPropertyValues = this.filterPropertyStore.getFilterPropertyValues(filterConfig);
		const dropdownValues = [];

		for (const filterMappingItem of this.items) {
			const filterValueMatch = filterPropertyValues.find(
				(filterValue) => filterValue.label === filterMappingItem.label,
			);
			if (filterValueMatch && filterValueMatch.isSelected) {
				dropdownValues.push(filterMappingItem);
			}
		}
		return dropdownValues;
	}
}
